import React, { useEffect, useState } from 'react';
import PlannersTable from '../components/PlannersTable';
import FlashMessage from '../components/FlashMessage';
import apiService from '../services/apiService';
import {
  PageContainer, ContentContainer, Header, AddButton, Label,
  PlannerFormWrapper, FormRow, SubmitButton,
  InputField,
  CheckBox,
  AllocationInput
} from '../styles/ComponentStyles';
import ViewComparison from '../components/ViewComparison';
import { getPaymentStatus } from '../utils/billStatusHelper';

const Planners = ({ isSidebarOpen }) => {
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageLimit, setCurrentPageLimit] = useState(10);
  const [flashMessage, setFlashMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    planner_name: '',
    which_is_higher: '',
    difference: '',
    total_expenses: '',
    total_cash_flows: '',
    which_is_higher_allocation: '',
    difference_allocation: '',
    expenses: [],
    cash_flows: [],
  });

  const [expenses, setExpenses] = useState([]);
  const [bills, setBills] = useState([]);
  const [cashFlows, setCashFlows] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [selectedCashFlows, setSelectedCashFlows] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [plannerData, setPlannerData] = useState(null);
  const [firstCutoffDay, setFirstCutoffDay] = useState('');
  const [secondCutoffDay, setSecondCutoffDay] = useState('');

  // Helper function to determine cutoff period based on day of month
  const getCutoffPeriod = (dateString) => {
    if (!firstCutoffDay || !secondCutoffDay || !dateString) return '';

    const date = new Date(dateString);
    const dayOfMonth = date.getDate();
    const firstDay = parseInt(firstCutoffDay);
    const secondDay = parseInt(secondCutoffDay);

    if (dayOfMonth >= firstDay && dayOfMonth < secondDay) {
      return '1st';
    } else if (dayOfMonth >= secondDay) {
      return '2nd';
    }
    return '';
  };

  let [difference, setDifference] = useState(0);
  let [differenceAllocation, setDifferenceAllocation] = useState(0);
  let [totalExpenses, setTotalExpenses] = useState(0);
  let [totalBills, setTotalBills] = useState(0);
  let [totalCashFlows, setTotalCashFlows] = useState(0);
  let [totalAllocation, setTotalAllocation] = useState(0);
  let [totalAllocationBills, setTotalAllocationBills] = useState(0);
  let [totalAllocationExpenses, setTotalAllocationExpenses] = useState(0);
  let [whichIsHigher, setWhichIsHigher] = useState('None');
  let [whichIsHigherAllocation, setWhichIsHigherAllocation] = useState('None');

  // Function to toggle the visibility of comparison and fetch planner data
  const toggleComparison = async (plannerId) => {
    setShowComparison((prevState) => !prevState);  // Toggle visibility
    try {
      const result = await apiService.getPlanner(plannerId);  // Fetch planner data
      setPlannerData(result);  // Set the planner data for comparison
      console.log("planner data: " + result);
    } catch (error) {
      console.error('Error fetching planner data:', error);
    }
  };

  useEffect(() => {
    const fetchPlanners = async () => {
      const result = await apiService.getPlanners(currentPage, currentPageLimit);
      setTotalItems(result.total_items || 0);
      setTotalPages(result.total_pages || 0);
    };
    fetchPlanners();
  }, [currentPage, currentPageLimit]);

  useEffect(() => {
    let newTotalExpenses = selectedExpenses.reduce((total, expense) => total + expense.amount, 0);
    let newTotalBills = selectedBills.reduce((total, bill) => total + bill.amount, 0);
    let newTotalCashFlows = selectedCashFlows.reduce((total, cashFlow) => total + cashFlow.amount, 0);
    newTotalCashFlows = newTotalCashFlows.toFixed(2)
    newTotalExpenses = newTotalExpenses.toFixed(2)
    setTotalExpenses(newTotalExpenses);  // Set the new total expenses
    setTotalBills(newTotalBills);  // Set the new total expenses
    setTotalCashFlows(newTotalCashFlows); // Set the new total cash flows
  }, [selectedExpenses, selectedBills, selectedCashFlows]);  // Trigger when selectedExpenses or selectedCashFlows change

  // Recalculate difference when totals change
  useEffect(() => {
    const expenses = parseFloat(totalExpenses) || 0;
    const bills = parseFloat(totalBills) || 0;
    const cashFlows = parseFloat(totalCashFlows) || 0;

    const total = expenses + bills;

    if (total > cashFlows) {
      setWhichIsHigher('Expenses+Bills');
      setDifference((total - cashFlows).toFixed(2));
    } else if (cashFlows > total) {
      setWhichIsHigher('Cash Flows');
      setDifference((cashFlows - total).toFixed(2));
    } else {
      setWhichIsHigher('Equal');
      setDifference(0);
    }
  }, [totalExpenses, totalBills, totalCashFlows]);

  useEffect(() => {
    const allocationTotal =
      (parseFloat(totalAllocationExpenses) || 0) +
      (parseFloat(totalAllocationBills) || 0);

    const cashFlows = parseFloat(totalCashFlows) || 0;

    if (allocationTotal > cashFlows) {
      setWhichIsHigherAllocation('Expenses+Bills');
      setDifferenceAllocation((allocationTotal - cashFlows).toFixed(2));
    } else if (cashFlows > allocationTotal) {
      setWhichIsHigherAllocation('Cash Flows');
      setDifferenceAllocation((cashFlows - allocationTotal).toFixed(2));
    } else {
      setWhichIsHigherAllocation('Equal');
      setDifferenceAllocation(0);
    }
  }, [totalAllocationBills, totalAllocationExpenses, totalCashFlows]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageLimitChange = (newLimit) => {
    setCurrentPageLimit(newLimit);
  };

  const handleFlashMessage = (message) => {
    setFlashMessage(message);
    setTimeout(() => {
      setFlashMessage('');
    }, 3000);
  };

  const handleAddPlannerClick = async () => {
    setShowForm(!showForm);

    if (!showForm) {
      try {
        const result = await apiService.getPlannersOptions();
        setExpenses(result.data.options.expenses || []);
        setBills(result.data.options.bills || []);
        setCashFlows(result.data.options.cash_flows || []);
      } catch (error) {
        console.error('Error fetching expenses & cash flows:', error);
        handleFlashMessage('Error loading options.');
      }
    }
  };

  const handleSelectBill = (bill) => {
    setSelectedBills((prevBills) => {
      if (prevBills.some((item) => item._id === bill._id)) {
        return prevBills.filter((item) => item._id !== bill._id);
      }
      return [...prevBills, { ...bill, allocation: 0 }];
    });
  };

  const handleSelectExpense = (expense) => {
    setSelectedExpenses((prevExpenses) => {
      if (prevExpenses.some((item) => item._id === expense._id)) {
        return prevExpenses.filter((item) => item._id !== expense._id);
      }
      return [...prevExpenses, { ...expense, allocation: 0 }];
    });
  };

  const handleExpenseAllocationChange = (expenseId, value) => {
    const allocation = parseFloat(value) || 0;
    setSelectedExpenses((prevExpenses) =>
      prevExpenses.map((item) =>
        item._id === expenseId ? { ...item, allocation } : item
      )
    );
  };

  const handleBillAllocationChange = (billId, value) => {
    const allocation = parseFloat(value) || 0;
    setSelectedBills((prevBills) =>
      prevBills.map((item) =>
        item._id === billId ? { ...item, allocation } : item
      )
    );
  };

  useEffect(() => {
    const newTotalAllocationBills =
      selectedBills.reduce(
        (total, item) => total + (item.allocation || 0),
        0
      );
    const newTotalAllocationExpenses =
      selectedExpenses.reduce(
        (total, item) => total + (item.allocation || 0),
        0
      );
    setTotalAllocationBills(newTotalAllocationBills.toFixed(2));
    setTotalAllocationExpenses(newTotalAllocationExpenses.toFixed(2));
    setTotalAllocation((newTotalAllocationBills + newTotalAllocationExpenses).toFixed(2));
  }, [selectedExpenses, selectedBills]);

  const handleSelectCashFlow = (cashFlow) => {
    setSelectedCashFlows((prevCashFlows) => {
      if (prevCashFlows.some((item) => item._id === cashFlow._id)) {
        return prevCashFlows.filter((item) => item._id !== cashFlow._id); // Remove the cash flow
      }
      return [...prevCashFlows, cashFlow]; // Add the cash flow
    });
  };

  // Select All handlers
  const handleSelectAllBills = () => {
    if (selectedBills.length === bills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(bills.map(bill => ({ ...bill, allocation: 0 })));
    }
  };

  const handleSelectAllExpenses = () => {
    if (selectedExpenses.length === expenses.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(expenses.map(expense => ({ ...expense, allocation: 0 })));
    }
  };

  const handleSelectAllCashFlows = () => {
    if (selectedCashFlows.length === cashFlows.length) {
      setSelectedCashFlows([]);
    } else {
      setSelectedCashFlows(cashFlows);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  }

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent page reload

    const plannerData = {
      planner_name: formData.planner_name,
      expenses: selectedExpenses,
      cash_flows: selectedCashFlows,
      which_is_higher: whichIsHigher,
      difference: difference,
      total_expenses: totalExpenses,
      total_cash_flows: totalCashFlows,
      which_is_higher_allocation: whichIsHigherAllocation,
      difference_allocation: differenceAllocation,
    };

    try {
      const response = await apiService.postPlanners(plannerData);
      const result = await response.json();

      if (response.ok) {
        handleFlashMessage(result.message);
        setFormData({
          planner_name: '',
          which_is_higher: '',
          difference: '',
          total_expenses: '',
          total_cash_flows: '',
          which_is_higher_allocation: '',
          difference_allocation: '',
          expenses: [],
          cash_flows: []
        });
        setSelectedExpenses([]);
        setSelectedCashFlows([]);
        setShowForm(false);
        handleRefresh();
      } else {
        handleFlashMessage(`Error: ${result.detail || 'Failed to save planner'}`);
      }
    } catch (error) {
      console.error('Error submitting planner:', error);
      handleFlashMessage('An error occurred while saving the planner.');
    }
  };

  const handleDelete = async (plannerId) => {
    const response = await apiService.deletePlanner(plannerId);
    try {
      handleFlashMessage(response.data.message + " Refreshing the page.");
      setRefreshKey(prevKey => prevKey + 1);
    } catch (error) {
      console.error(error);
      handleFlashMessage(response.detail + " Refreshing the page.");
    }
  };

  return (
    <PageContainer>
      <ContentContainer isSidebarOpen={isSidebarOpen}>
        <Header>
          <h1>Manage Planners</h1>
          <AddButton onClick={handleAddPlannerClick}>
            {showForm ? 'Cancel' : 'Add New Planner'}
          </AddButton>
        </Header>

        <div>
          <Label>Total Planners: {totalItems}</Label>
          <Label>Total Pages: {totalPages}</Label>
        </div>

        {flashMessage && <FlashMessage message={flashMessage} />}

        <PlannersTable
          handleFlashMessage={handleFlashMessage}
          refreshKey={refreshKey}
          setRefreshKey={setRefreshKey}
          handleDelete={handleDelete}
          currentPage={currentPage}
          currentPageLimit={currentPageLimit}
          onPageChange={handlePageChange}
          onPageLimitChange={handlePageLimitChange}
          toggleComparison={toggleComparison}
        />

        {showForm && (
          <PlannerFormWrapper onSubmit={handleSubmit}>
            <h3>Add New Planner</h3>

            <FormRow>
              <label htmlFor="planner_name">Planner Name*</label>
              <InputField
                id="planner_name"
                name="planner_name"
                value={formData.planner_name || ''}
                onChange={(e) => setFormData({ ...formData, planner_name: e.target.value })}
              />
            </FormRow>

            <FormRow>
              <label htmlFor="first_cutoff_day">First Cutoff Day (1-31)*</label>
              <InputField
                id="first_cutoff_day"
                name="first_cutoff_day"
                type="number"
                min="1"
                max="31"
                placeholder="e.g., 1"
                value={firstCutoffDay}
                onChange={(e) => setFirstCutoffDay(e.target.value)}
              />
            </FormRow>

            <FormRow>
              <label htmlFor="second_cutoff_day">Second Cutoff Day (1-31)*</label>
              <InputField
                id="second_cutoff_day"
                name="second_cutoff_day"
                type="number"
                min="1"
                max="31"
                placeholder="e.g., 20"
                value={secondCutoffDay}
                onChange={(e) => setSecondCutoffDay(e.target.value)}
              />
            </FormRow>

            <h4>Summary</h4>
            <table border="1" width="100%">
              <thead>
                <tr>
                  <th>Total Bills</th>
                  <th>Total Expenses</th>
                  <th>Total Cash Flows</th>
                  <th>Allocation Bills</th>
                  <th>Allocation Expenses</th>
                  <th>Total Allocations</th>
                  <th>Which is Higher?</th>
                  <th>Difference</th>
                  <th style={{ color: 'green' }}>Which is Higher?(Allocation)</th>
                  <th>Difference(Allocation)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{totalBills}</td>
                  <td>{totalExpenses}</td>
                  <td>{totalCashFlows}</td>
                  <td>{totalBills}</td>
                  <td>{totalExpenses}</td>
                  <td>{totalAllocation}</td>
                  <td>
                    <strong>
                      {whichIsHigher}
                    </strong>
                  </td>
                  <td>{difference}</td>
                  <td style={{ color: 'green' }}><strong>{whichIsHigherAllocation}</strong></td>
                  <td>{differenceAllocation}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {/* Left Side: Bills, Cash Flows, and Expenses */}
              <div style={{ flex: 1, fontSize: '0.85rem', minWidth: '280px' }}>
                {/* Bills Table */}
                <h4>Bills <button onClick={handleSelectAllBills} style={{ marginLeft: '10px', fontSize: '0.75rem' }}>{selectedBills.length === bills.length ? 'Deselect All' : 'Select All'}</button></h4>
                <table border="1" width="100%" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Due Date</th>
                      <th>Cutoff</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bills || []).map((bill, index) => {
                      const cutoff = getCutoffPeriod(bill.due_date);
                      const paymentStatus = getPaymentStatus(bill);
                      return (
                        <tr key={index}>
                          <td>
                            <CheckBox
                              type="checkbox"
                              checked={selectedBills.some((item) => item._id === bill._id)}
                              onChange={() => handleSelectBill(bill)}
                            />
                          </td>
                          <td>{bill.bill_label}</td>
                          <td>{bill.amount}</td>
                          <td>{bill.due_date}</td>
                          <td><strong>{cutoff}</strong></td>
                          <td>
                            <span style={{
                              backgroundColor: paymentStatus.color,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              {paymentStatus.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>


                {/* Cash Flows Table */}
                <h4>Cash Flows <button onClick={handleSelectAllCashFlows} style={{ marginLeft: '10px', fontSize: '0.75rem' }}>{selectedCashFlows.length === cashFlows.length ? 'Deselect All' : 'Select All'}</button></h4>
                <table border="1" width="100%" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Cutoff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cashFlows || []).map((cashFlow, index) => {
                      const cutoff = getCutoffPeriod(cashFlow.date_of_transaction);
                      return (
                        <tr key={index}>
                          <td>
                            <CheckBox
                              type="checkbox"
                              checked={selectedCashFlows.some((item) => item._id === cashFlow._id)}
                              onChange={() => handleSelectCashFlow(cashFlow)}
                            />
                          </td>
                          <td>{cashFlow.cash_flow_label}</td>
                          <td>{cashFlow.amount}</td>
                          <td>{cashFlow.date_of_transaction}</td>
                          <td><strong>{cutoff}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Expenses Table - Moved to bottom */}
                <h4>Expenses <button onClick={handleSelectAllExpenses} style={{ marginLeft: '10px', fontSize: '0.75rem' }}>{selectedExpenses.length === expenses.length ? 'Deselect All' : 'Select All'}</button></h4>
                <table border="1" width="100%" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Cutoff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(expenses || []).map((expense, index) => {
                      const cutoff = getCutoffPeriod(expense.date_of_transaction);
                      return (
                        <tr key={index}>
                          <td>
                            <CheckBox
                              type="checkbox"
                              checked={selectedExpenses.some((item) => item._id === expense._id)}
                              onChange={() => handleSelectExpense(expense)}
                            />
                          </td>
                          <td>{expense.expense_label}</td>
                          <td>{expense.amount}</td>
                          <td>{expense.date_of_transaction}</td>
                          <td><strong>{cutoff}</strong></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Right Side: Selected Bills, Cash Flows, and Expenses */}
              <div style={{ flex: 1, fontSize: '0.85rem', minWidth: '280px' }}>
                <h4>Selected Bills</h4>
                <table border="1" width="100%" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBills.map((bill, index) => (
                      <tr key={bill._id}>
                        <td>{bill.bill_label}</td>
                        <td>{bill.amount}</td>
                        <td>
                          <AllocationInput
                            type='number'
                            value={bill.allocation}
                            onChange={(e) => handleBillAllocationChange(bill._id, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>Total Bills</strong></td>
                      <td><strong>{totalBills}</strong></td>
                      <td><strong>{totalAllocationBills}</strong></td>
                    </tr>
                  </tbody>
                </table>

                <h4>Selected Cash Flows</h4>
                <table border="1" width="100%" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCashFlows.map((cashFlow, index) => (
                      <tr key={cashFlow._id}>
                        <td>{cashFlow.cash_flow_label}</td>
                        <td>{cashFlow.amount}</td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>Total Cash Flows</strong></td>
                      <td><strong>{totalCashFlows}</strong></td>
                    </tr>
                  </tbody>
                </table>

                <h4>Selected Expenses</h4>
                <table border="1" width="100%" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedExpenses.map((expense, index) => (
                      <tr key={expense._id}>
                        <td>{expense.expense_label}</td>
                        <td>{expense.amount}</td>
                        <td>
                          <AllocationInput
                            type='number'
                            value={expense.allocation}
                            onChange={(e) => handleExpenseAllocationChange(expense._id, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td><strong>Total Expenses</strong></td>
                      <td><strong>{totalExpenses}</strong></td>
                      <td><strong>{totalAllocationExpenses}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <SubmitButton type="submit">Save</SubmitButton>
          </PlannerFormWrapper>
        )}

        <div>
          {/* Display the comparison section if visible */}
          {showComparison && plannerData && (
            <div style={{ marginTop: '20px' }}>
              <h3>Comparison for Planner: {plannerData.planner_name}</h3>
              <ViewComparison plannerData={plannerData} />  {/* Show comparison data */}
            </div>
          )}

        </div>

      </ContentContainer>
    </PageContainer>
  );
};

export default Planners;
